import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Check,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Volume2,
} from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  getAgentVoiceCatalog,
  getAgentVoicePreview,
  resolveAgentVoiceProfile,
} from '../services/api.ts';
import type { AgentVoice, AgentVoiceGender } from '../services/api.ts';

export interface AgentVoiceSelection {
  provider: 'elevenlabs';
  voice_id: string;
  voice_name: string;
  voice_gender: AgentVoiceGender;
  locale: 'pt-BR';
}

interface AgentVoiceSelectorProps {
  assistantName: string;
  value: AgentVoiceSelection | null;
  persistedAssistantName?: string;
  persistedValue?: AgentVoiceSelection | null;
  onChange: (voice: AgentVoiceSelection | null) => void;
  onValidityChange?: (isValid: boolean) => void;
}

const genderLabels: Record<AgentVoiceGender, string> = {
  female: 'femininas',
  male: 'masculinas',
};

const genderSummaryLabels: Record<AgentVoiceGender, string> = {
  female: 'Feminina',
  male: 'Masculina',
};

const PROFILE_RESOLUTION_DEBOUNCE_MS = 450;

const AgentVoiceSelector: React.FC<AgentVoiceSelectorProps> = ({
  assistantName,
  value,
  persistedAssistantName = '',
  persistedValue = null,
  onChange,
  onValidityChange,
}) => {
  const { isDark } = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const previewRequestRef = useRef(0);
  const profileRequestRef = useRef(0);
  const normalizedNameRef = useRef('');
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onValidityChangeRef = useRef(onValidityChange);
  const persistedAssistantNameRef = useRef(persistedAssistantName);
  const persistedValueRef = useRef(persistedValue);

  const [resolvedGender, setResolvedGender] = useState<AgentVoiceGender | null>(null);
  const [profileSource, setProfileSource] = useState<'explicit' | 'name' | 'ambiguous' | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [profileRetry, setProfileRetry] = useState(0);
  const previousProfileRetryRef = useRef(profileRetry);
  const [explicitGenderLoading, setExplicitGenderLoading] = useState<AgentVoiceGender | null>(null);

  const [voices, setVoices] = useState<AgentVoice[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogRetry, setCatalogRetry] = useState(0);

  const [activeVoiceId, setActiveVoiceId] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [previewErrorId, setPreviewErrorId] = useState<string | null>(null);
  const radioGroupId = useId();

  valueRef.current = value;
  onChangeRef.current = onChange;
  onValidityChangeRef.current = onValidityChange;
  persistedAssistantNameRef.current = persistedAssistantName;
  persistedValueRef.current = persistedValue;

  const releaseObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const stopPreview = useCallback(() => {
    previewRequestRef.current += 1;
    const audio = audioRef.current;
    if (audio?.getAttribute('src')) {
      audio.pause();
      audio.removeAttribute('src');
    }
    releaseObjectUrl();
    setActiveVoiceId(null);
    setPreviewLoadingId(null);
  }, [releaseObjectUrl]);

  useEffect(() => () => {
    previewRequestRef.current += 1;
    profileRequestRef.current += 1;
    const audio = audioRef.current;
    if (audio?.getAttribute('src')) {
      audio.pause();
      audio.removeAttribute('src');
    }
    releaseObjectUrl();
  }, [releaseObjectUrl]);

  const normalizedName = assistantName.trim();
  normalizedNameRef.current = normalizedName;

  useEffect(() => {
    let active = true;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const requestId = profileRequestRef.current + 1;
    profileRequestRef.current = requestId;
    const requestedName = normalizedName;
    const isControlledRetry = profileRetry !== previousProfileRetryRef.current;
    previousProfileRetryRef.current = profileRetry;

    const isCurrentRequest = () => (
      active
      && profileRequestRef.current === requestId
      && normalizedNameRef.current === requestedName
    );

    stopPreview();
    setProfileError(false);
    setProfileSource(null);
    setExplicitGenderLoading(null);

    if (!requestedName) {
      setResolvedGender(null);
      setVoices([]);
      setProfileLoading(false);
      return () => {
        active = false;
      };
    }

    const persistedVoice = persistedValueRef.current;
    if (
      persistedVoice
      && valueRef.current?.voice_id === persistedVoice.voice_id
      && valueRef.current.voice_gender === persistedVoice.voice_gender
      && requestedName === persistedAssistantNameRef.current.trim()
    ) {
      setResolvedGender(persistedVoice.voice_gender);
      setProfileSource('explicit');
      setProfileLoading(false);
      return () => {
        active = false;
      };
    }

    setResolvedGender(null);
    setVoices([]);
    setProfileLoading(true);

    const resolveProfile = () => {
      resolveAgentVoiceProfile(requestedName)
        .then((profile) => {
          if (!isCurrentRequest()) return;
          setResolvedGender(profile.gender);
          setProfileSource(profile.source);

          const currentVoice = valueRef.current;
          if (
            currentVoice
            && (!profile.gender || currentVoice.voice_gender !== profile.gender)
          ) {
            onChangeRef.current(null);
          }
        })
        .catch(() => {
          if (!isCurrentRequest()) return;
          setResolvedGender(null);
          setProfileError(true);
        })
        .finally(() => {
          if (isCurrentRequest()) setProfileLoading(false);
        });
    };

    if (isControlledRetry) {
      resolveProfile();
    } else {
      debounceTimer = setTimeout(resolveProfile, PROFILE_RESOLUTION_DEBOUNCE_MS);
    }

    return () => {
      active = false;
      if (debounceTimer !== null) clearTimeout(debounceTimer);
    };
  }, [normalizedName, profileRetry, stopPreview]);

  useEffect(() => {
    let active = true;
    stopPreview();
    setCatalogError(false);
    setCatalogLoaded(false);
    setPreviewErrorId(null);

    if (!resolvedGender) {
      setVoices([]);
      setCatalogLoading(false);
      return () => {
        active = false;
      };
    }

    setCatalogLoading(true);
    setVoices([]);
    getAgentVoiceCatalog(resolvedGender)
      .then((catalog) => {
        if (!active) return;
        setVoices(catalog.voices.filter((voice) => (
          voice.gender === resolvedGender && voice.locale === 'pt-BR'
        )));
        setCatalogLoaded(true);
      })
      .catch(() => {
        if (!active) return;
        setCatalogError(true);
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogRetry, resolvedGender, stopPreview]);

  const handleGenderChoice = async (gender: AgentVoiceGender) => {
    const requestedName = normalizedNameRef.current;
    if (!requestedName || explicitGenderLoading) return;

    const requestId = profileRequestRef.current + 1;
    profileRequestRef.current = requestId;
    const isCurrentRequest = () => (
      profileRequestRef.current === requestId
      && normalizedNameRef.current === requestedName
    );

    stopPreview();
    setProfileError(false);
    setExplicitGenderLoading(gender);
    try {
      const profile = await resolveAgentVoiceProfile(requestedName, gender);
      if (!isCurrentRequest()) return;
      const nextGender = profile.gender || gender;
      setResolvedGender(nextGender);
      setProfileSource('explicit');
      setCatalogRetry((attempt) => attempt + 1);
      if (valueRef.current && valueRef.current.voice_gender !== nextGender) {
        onChangeRef.current(null);
      }
    } catch (_error) {
      if (isCurrentRequest()) setProfileError(true);
    } finally {
      if (isCurrentRequest()) setExplicitGenderLoading(null);
    }
  };

  const handleSelection = (voice: AgentVoice) => {
    onChange({
      provider: 'elevenlabs',
      voice_id: voice.voice_id,
      voice_name: voice.name,
      voice_gender: voice.gender,
      locale: 'pt-BR',
    });
  };

  const handlePreview = async (voice: AgentVoice) => {
    setPreviewErrorId(null);
    if (activeVoiceId === voice.voice_id) {
      stopPreview();
      return;
    }

    stopPreview();
    const requestId = previewRequestRef.current;
    setPreviewLoadingId(voice.voice_id);
    try {
      const preview = await getAgentVoicePreview(voice.voice_id);
      if (requestId !== previewRequestRef.current) return;
      const objectUrl = URL.createObjectURL(preview);
      objectUrlRef.current = objectUrl;

      const audio = audioRef.current;
      if (!audio) {
        releaseObjectUrl();
        return;
      }

      audio.src = objectUrl;
      audio.load();
      setActiveVoiceId(voice.voice_id);
      setPreviewLoadingId(null);
      await audio.play();
    } catch (_error) {
      if (requestId !== previewRequestRef.current) return;
      stopPreview();
      setPreviewErrorId(voice.voice_id);
    }
  };

  const selectedVoiceRemoved = useMemo(() => (
    Boolean(value)
    && Boolean(resolvedGender)
    && value?.voice_gender === resolvedGender
    && !catalogLoading
    && !catalogError
    && catalogLoaded
    && !voices.some((voice) => voice.voice_id === value?.voice_id)
  ), [catalogError, catalogLoaded, catalogLoading, resolvedGender, value, voices]);

  const isUnchangedPersistedSelection = Boolean(
    value
    && persistedValue
    && value.voice_id === persistedValue.voice_id
    && value.voice_gender === persistedValue.voice_gender
    && normalizedName === persistedAssistantName.trim()
  );

  const voiceSelectionValid = !value || (
    catalogLoaded
      ? Boolean(
        normalizedName
        && resolvedGender
        && !profileLoading
        && !profileError
        && !catalogError
        && value.voice_gender === resolvedGender
        && voices.some((voice) => voice.voice_id === value.voice_id)
      )
      : isUnchangedPersistedSelection
  );

  useEffect(() => {
    onValidityChangeRef.current?.(voiceSelectionValid);
  }, [voiceSelectionValid]);

  const textPrimary = isDark ? 'text-gray-200' : 'text-gray-800';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600';

  return (
    <section
      aria-labelledby="agent-voice-title"
      className={`rounded-2xl border p-4 ${
        isDark ? 'border-gray-600 bg-gray-800/70' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-4 flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sminy-primary/10 text-sminy-primary">
          <Volume2 className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h3 id="agent-voice-title" className={`text-sm font-semibold ${textPrimary}`}>
            Voz do agente
          </h3>
          <p className={`mt-1 text-xs leading-5 ${textSecondary}`}>
            Escolha uma voz profissional com prévia em português do Brasil.
          </p>
        </div>
      </div>

      {!normalizedName && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          isDark
            ? 'border-gray-600 bg-gray-700/50 text-gray-300'
            : 'border-gray-200 bg-gray-50 text-gray-600'
        }`}>
          Informe o nome do agente e saia do campo para ver as vozes disponíveis.
        </div>
      )}

      {normalizedName && profileLoading && (
        <div role="status" className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${textSecondary}`}>
          <Loader2 className="h-4 w-4 animate-spin text-sminy-primary" aria-hidden="true" />
          Identificando o perfil de voz…
        </div>
      )}

      {normalizedName && profileError && (
        <div role="alert" className={`rounded-xl border p-4 ${
          isDark
            ? 'border-red-800/70 bg-red-950/30 text-red-200'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          <div className="flex items-start gap-2 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>Não foi possível identificar o perfil da voz.</p>
          </div>
          <button
            type="button"
            onClick={() => setProfileRetry((attempt) => attempt + 1)}
            className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-current px-3 py-2 text-sm font-medium transition-colors hover:bg-red-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Tentar novamente
          </button>
          <div className={`mt-4 border-t pt-4 ${isDark ? 'border-red-800/70' : 'border-red-200'}`}>
            <p className="mb-2 text-xs">
              Se preferir, escolha o perfil manualmente para continuar.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(['female', 'male'] as AgentVoiceGender[]).map((gender) => (
                <button
                  key={gender}
                  type="button"
                  disabled={Boolean(explicitGenderLoading)}
                  onClick={() => handleGenderChoice(gender)}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-current px-3 py-2 text-sm font-medium transition-colors hover:bg-red-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:cursor-wait disabled:opacity-60"
                >
                  {explicitGenderLoading === gender && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  {genderSummaryLabels[gender]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {normalizedName && !profileLoading && !profileError && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className={`text-sm ${textSecondary}`} aria-live="polite">
              {resolvedGender
                ? `Mostrando vozes ${genderLabels[resolvedGender]} para ${normalizedName}.`
                : `O nome ${normalizedName} pode ter mais de um perfil. Escolha qual voz deseja ver.`}
            </p>
            {resolvedGender && profileSource === 'name' && (
              <span className="w-fit rounded-full bg-sminy-primary/10 px-2.5 py-1 text-xs font-semibold text-sminy-primary">
                Sugestão automática
              </span>
            )}
          </div>

          <fieldset>
            <legend className={`mb-2 text-xs font-medium ${textPrimary}`}>
              Perfil da voz
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {(['female', 'male'] as AgentVoiceGender[]).map((gender) => {
                const isSelected = resolvedGender === gender;
                const isLoading = explicitGenderLoading === gender;
                return (
                  <button
                    key={gender}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={Boolean(explicitGenderLoading)}
                    onClick={() => handleGenderChoice(gender)}
                    className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:cursor-wait disabled:opacity-60 ${
                      isSelected
                        ? 'border-sminy-primary bg-sminy-primary/10 text-sminy-primary'
                        : isDark
                          ? 'border-gray-600 bg-gray-700 text-gray-200 hover:border-sminy-primary/60'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-sminy-primary/60'
                    }`}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : isSelected ? (
                      <Check className="h-4 w-4" aria-hidden="true" />
                    ) : null}
                    {genderSummaryLabels[gender]}
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>
      )}

      {resolvedGender && !profileError && (
        <div className="mt-4">
          {catalogLoading && (
            <div role="status" aria-label="Carregando vozes" className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {[0, 1].map((item) => (
                <div
                  key={item}
                  className={`h-32 animate-pulse rounded-2xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}
                />
              ))}
            </div>
          )}

          {!catalogLoading && catalogError && (
            <div role="alert" className={`rounded-xl border p-4 ${
              isDark
                ? 'border-red-800/70 bg-red-950/30 text-red-200'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}>
              <p className="text-sm">Não foi possível carregar as vozes em português do Brasil.</p>
              <button
                type="button"
                onClick={() => setCatalogRetry((attempt) => attempt + 1)}
                className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-current px-3 py-2 text-sm font-medium transition-colors hover:bg-red-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Tentar novamente
              </button>
            </div>
          )}

          {selectedVoiceRemoved && (
            <div role="alert" className={`mb-3 flex items-start gap-2 rounded-xl border p-3 text-sm ${
              isDark
                ? 'border-amber-700/60 bg-amber-950/20 text-amber-200'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}>
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>
                A voz <strong>{value?.voice_name || 'selecionada'}</strong> não está mais disponível. Escolha outra voz para manter os áudios ativos.
              </p>
            </div>
          )}

          {!catalogLoading && !catalogError && voices.length === 0 && (
            <div className={`rounded-xl border px-4 py-8 text-center ${
              isDark ? 'border-gray-600 bg-gray-700/40' : 'border-gray-200 bg-gray-50'
            }`}>
              <Volume2 className={`mx-auto h-8 w-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} aria-hidden="true" />
              <p className={`mt-3 text-sm font-medium ${textPrimary}`}>Nenhuma voz disponível</p>
              <p className={`mt-1 text-xs ${textSecondary}`}>
                Ainda não há uma voz {genderSummaryLabels[resolvedGender].toLowerCase()} com prévia em português do Brasil.
              </p>
            </div>
          )}

          {!catalogLoading && !catalogError && voices.length > 0 && (
            <fieldset
              className="grid grid-cols-1 gap-3 md:grid-cols-2"
            >
              <legend className="sr-only">
                Vozes {genderLabels[resolvedGender]} em português do Brasil
              </legend>
              {voices.map((voice, index) => {
                const isSelected = value?.voice_id === voice.voice_id;
                const isPlaying = activeVoiceId === voice.voice_id;
                const isPreviewLoading = previewLoadingId === voice.voice_id;
                const previewFailed = previewErrorId === voice.voice_id;
                const inputId = `${radioGroupId}-voice-${index}`;
                return (
                  <div
                    key={voice.voice_id}
                    className={`grid grid-cols-[1fr_auto] items-end gap-3 rounded-2xl border p-4 transition-colors ${
                      isSelected
                        ? 'border-sminy-primary bg-sminy-primary/10'
                        : isDark
                          ? 'border-gray-600 bg-gray-700/40 hover:border-sminy-primary/60'
                          : 'border-gray-200 bg-white hover:border-sminy-primary/60'
                    }`}
                  >
                    <input
                      id={inputId}
                      type="radio"
                      name={`${radioGroupId}-voice`}
                      value={voice.voice_id}
                      checked={isSelected}
                      onChange={() => handleSelection(voice)}
                      className="peer sr-only"
                    />
                    <label
                      htmlFor={inputId}
                      className="col-span-2 block cursor-pointer rounded-xl focus:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-sminy-primary peer-focus-visible:ring-offset-2"
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className={`font-semibold ${textPrimary}`}>{voice.name}</span>
                            {isSelected && (
                              <span className="rounded-full bg-sminy-primary px-2 py-0.5 text-[11px] font-semibold text-white">
                                Selecionada
                              </span>
                            )}
                          </span>
                          <span className={`mt-1 block text-xs ${textSecondary}`}>
                            {[voice.accent, voice.description].filter(Boolean).join(' · ') || 'Voz profissional'}
                          </span>
                        </span>
                        <span className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                          isSelected ? 'border-[5px] border-sminy-primary' : isDark ? 'border-gray-500' : 'border-gray-300'
                        }`} aria-hidden="true" />
                      </span>
                    </label>

                    <span className={`w-fit rounded-full px-2.5 py-1 text-[11px] font-medium ${
                      isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-600'
                    }`}>
                      Português do Brasil
                    </span>
                    <button
                      type="button"
                      onClick={() => handlePreview(voice)}
                      disabled={isPreviewLoading}
                      aria-label={isPlaying ? `Pausar prévia de ${voice.name}` : `Ouvir prévia de ${voice.name}`}
                      className={`inline-flex min-h-11 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:cursor-wait disabled:opacity-60 ${
                        isDark
                          ? 'bg-gray-600 text-gray-100 hover:bg-gray-500'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {isPreviewLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : isPlaying ? (
                        <Pause className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Play className="h-4 w-4" aria-hidden="true" />
                      )}
                      {isPreviewLoading ? 'Carregando' : isPlaying ? 'Pausar' : 'Ouvir prévia'}
                    </button>

                    {previewFailed && (
                      <p role="alert" className="col-span-2 text-xs text-red-500">
                        Não foi possível reproduzir esta prévia. Tente novamente.
                      </p>
                    )}
                  </div>
                );
              })}
            </fieldset>
          )}
        </div>
      )}

      <audio
        ref={audioRef}
        preload="none"
        className="hidden"
        onEnded={() => stopPreview()}
        onError={() => {
          if (activeVoiceId) setPreviewErrorId(activeVoiceId);
          stopPreview();
        }}
      />
    </section>
  );
};

export default AgentVoiceSelector;
